import React, { useEffect, useRef, useState } from "react";

const Layout8 = ({ data }) => {
    const {
        video,
        brand,
        qr,
        feature,
        canvas
    } = data;
    const [currentSlide, setCurrentSlide] = useState(0);
    const [videoDuration, setVideoDuration] = useState(null);

    const videoRef = useRef(null);
    const timerRef = useRef(null);

    const slides = feature?.slides || [];
    const slide = slides[currentSlide];
    const textColor = canvas?.qrTextColor || '#FFFFFF';
    const sliderTextColor = canvas?.sliderTextColor || '#FFFFFF';

    const nextSlide = () => {
        setCurrentSlide((prev) =>
            prev === slides.length - 1 ? 0 : prev + 1
        );
    };

    const previousSlide = () => {
        setCurrentSlide((prev) =>
            prev === 0 ? slides.length - 1 : prev - 1
        );
    };

    /*
     * Get video duration
     */
    const handleVideoLoaded = () => {
        if (videoRef.current) {
            const duration = videoRef.current.duration;

            if (duration && isFinite(duration)) {
                setVideoDuration(duration);
            }
        }
    };

    /*
     * Auto change slide based on video duration
     */
    useEffect(() => {
        // Clear previous timer
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        if (!slides.length) {
            return;
        }

        // If video duration is available
        if (videoDuration) {
            timerRef.current = setTimeout(() => {
                nextSlide();
            }, videoDuration * 1000);
        } else {
            // Fallback if video duration is not available
            timerRef.current = setTimeout(() => {
                nextSlide();
            }, 5000);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [currentSlide, videoDuration, slides.length]);

    /*
     * Reset video when slide changes
     */
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;

            const playPromise = videoRef.current.play();

            if (playPromise !== undefined) {
                playPromise.catch(() => { });
            }
        }
    }, [currentSlide]);

    return (
        <>
            <style>
                {`
                    @keyframes layout8SlideIn {
                        from {
                            transform: translateX(100%);
                        }

                        to {
                            transform: translateX(0);
                        }
                    }

                    .layout8-slide-image {
                        animation: layout8SlideIn 600ms ease-out;
                    }
                `}
            </style>
            <div
                className="w-full h-full flex overflow-hidden"
                style={{
                    background: "#000"
                }}
            >

                {/* LEFT SECTION */}
                <div
                    className="w-[62%] h-full flex flex-col"
                    style={{
                        backgroundColor:
                            canvas?.leftBackgroundColor || "#FFFFFF"
                    }}
                >

                    {/* BRAND LOGO */}
                    <div className="h-[15%] flex justify-center items-center">
                        {brand?.logoUrl && (
                            <img
                                src={brand.logoUrl}
                                alt="logo"
                                className="
                                max-h-[7rem]
                                object-contain
                            "
                            />
                        )}
                    </div>

                    {/* VIDEO */}
                    <div
                        className="
                        h-[70%]
                        w-full
                        overflow-hidden
                        flex
                        items-center
                        justify-center
                    "
                    >
                        {video?.url ? (
                            <video
                                ref={videoRef}
                                src={video.url}
                                autoPlay
                                muted
                                loop
                                playsInline
                                onLoadedMetadata={handleVideoLoaded}
                                className="
                                h-full
                                object-cover
                                w-[92%]
                            "
                            />
                        ) : (
                            <img
                                src={feature?.splashImage}
                                className="
                                w-full
                                h-full
                                object-cover
                            "
                                alt=""
                            />
                        )}
                    </div>

                    {/* QR SECTION */}
                    <div
                        className="
                        h-[30%]
                        flex
                        items-center
                        justify-center
                        gap-8
                    "
                    >
                        {qr?.imageUrl && (
                            <img
                                src={qr.imageUrl}
                                alt="qr"
                                className="
                                w-[10rem]
                                h-[10rem]
                            "
                            />
                        )}

                        <div
                            className="
                            text-3xl
                            font-bold
                            max-w-[220px]
                            leading-tight
                        "
                            style={{
                                color: textColor,
                            }}
                        >
                            {qr?.text}
                        </div>
                    </div>
                </div>

                {/* RIGHT SECTION */}
                <div
                    className="
                    w-[38%]
                    h-full
                    relative
                    overflow-hidden
                "
                    style={{
                        backgroundColor:
                            canvas?.rightBackgroundColor || "#0A1A3C",
                    }}
                >

                    {/* FEATURE BACKGROUND IMAGE */}
                    {slide?.imageUrl && (
                        <img
                            key={slide.imageUrl}
                            src={slide.imageUrl}
                            alt={slide.headline || "Feature"}
                            className="
                            absolute
                            inset-0
                            w-full
                            h-full
                            object-cover
                            layout8-slide-image
                        "
                        />
                    )}

                    {/* DARK OVERLAY */}
                    <div
                        className="
                        absolute
                        inset-0
                        bg-black/10
                    "
                    />

                    {/* CONTENT */}
                    <div
                        className="
                        relative
                        z-10
                        w-full
                        h-full
                        flex
                        flex-col
                    "
                    >

                        {/* HEADLINE */}
                        <div
                            className="
                            h-[10%]
                            flex
                            items-start
                            w-full
                            justify-center
                            mt-6
                        "
                        >
                            <div
                                className="
                                w-full
                                text-5xl
                                font-bold
                                tracking-wide
                                text-white
                                text-center
                                [text-shadow:2px_2px_6px_rgba(0,0,0,0.8)]
                            "
                                style={{
                                    color: sliderTextColor,
                                }}
                            >
                                {slide?.headline}
                            </div>
                        </div>

                        <div className="h-[45%]" />

                        {/* TEXT CONTENT */}
                        <div
                            className="
                            h-[35%]
                            flex
                            flex-col
                            justify-end
                            text-center
                        "
                        >
                            <h2
                                className="
                                text-3xl
                                font-bold
                                text-white
                                leading-tight
                                mb-2
                            "
                                style={{
                                    color: sliderTextColor,
                                }}
                            >
                                {slide?.title}
                            </h2>

                            <p
                                className="
                                text-md
                                font-normal
                                text-white
                                leading-relaxed
                                text-start
                                ml-2
                            "
                                style={{
                                    color: sliderTextColor,
                                }}
                            >
                                {slide?.description}
                            </p>
                        </div>

                        {/* SLIDER CONTROLS */}
                        <div
                            className="
                            h-[7%]
                            flex
                            items-center
                            justify-center
                            gap-4
                            pb-2
                        "
                        >

                            {/* LEFT ARROW */}
                            <button
                                type="button"
                                onClick={previousSlide}
                                className="
                                text-white
                                hover:opacity-70
                                transition-opacity
                            "
                                aria-label="Previous slide"
                            >
                                <svg
                                    width="28"
                                    height="28"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                            </button>

                            {/* DOTS */}
                            <div className="flex items-center gap-2">
                                {slides.map((_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() =>
                                            setCurrentSlide(index)
                                        }
                                        aria-label={`Go to slide ${index + 1}`}
                                        className={`
                                        rounded-full
                                        transition-all
                                        duration-300
                                        ${currentSlide === index
                                                ? "w-5 h-5 bg-[#f4324b]"
                                                : "w-4 h-4 bg-white/50"
                                            }
                                    `}
                                    />
                                ))}
                            </div>

                            {/* RIGHT ARROW */}
                            <button
                                type="button"
                                onClick={nextSlide}
                                className="
                                text-white
                                hover:opacity-70
                                transition-opacity
                            "
                                aria-label="Next slide"
                            >
                                <svg
                                    width="28"
                                    height="28"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Layout8;